// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.

import React, {useEffect, useState} from 'react'
import '../styles/app.scss'
import {BrowserRouter as Router, Route} from "react-router-dom"
import * as microsoftTeams from "@microsoft/teams-js"

import {CreatePresentationModule} from './Create/CreatePresentationModule'
import {Config} from './Config'
import {Provider, teamsDarkTheme, teamsHighContrastTheme, teamsTheme, ThemePrepared} from '@fluentui/react-northstar'
import {ChannelTabContent} from './ChannelTab/ChannelTabContent'
import {AuthLogin} from './Auth/AuthLogin'
import {AuthRegister} from './Auth/AuthRegister'
import SendstepsUserProvider from '../utils/UserContext'
import {ChannelTabSidePanel} from './ChannelTab/ChannelTabSidePanel'
import {DeleteQuizModal} from './DeleteQuizModal'

/**
 * The main app which handles the initialization and routing
 * of the app.
 */
const App = () => {

    microsoftTeams.initialize()

    const [context, setContext] = useState<microsoftTeams.Context>()
    const [theme, setTheme] = useState<ThemePrepared>()

    useEffect(() => {
        microsoftTeams.getContext(context => setContext(context))
    }, [])

    useEffect(() => themeChangeHandler(context?.theme), [context])

    const themeChangeHandler = (theme: string | undefined) => {
        if (theme === 'dark') setTheme(teamsDarkTheme)
        else if (theme === 'contrast') setTheme(teamsHighContrastTheme)
        else if (theme === 'default') setTheme(teamsTheme)
    }

    microsoftTeams.registerOnThemeChangeHandler(themeChangeHandler)

    // Display the app home page hosted in Teams
    if (theme && context?.userObjectId) return (
        <SendstepsUserProvider>
            <Provider theme={theme}>
                <Router>
                    <Route exact path="/config">
                        <Config context={context}/>
                    </Route>

                    <Route exact path="/channel-dashboard/:presenterId">
                        {
                            context.frameContext === 'content' ?
                                <ChannelTabContent context={context}/> :
                                <ChannelTabSidePanel context={context}/>
                        }
                    </Route>

                    <Route exact path="/create-quiz/:quizId?" component={CreatePresentationModule}/>
                    <Route exact path="/remove-quiz" component={DeleteQuizModal}/>

                    <Route exact path="/login" component={AuthLogin}/>
                    <Route exact path="/register" component={AuthRegister}/>
                    {/* <Route exact path="/request-password-reset" component={AuthForgotPass} /> */}
                </Router>
            </Provider>
        </SendstepsUserProvider>
    )
    else return (<div/>)
}

export default App
